import {
  ActionCreator,
  ActionType,
  createAction as ngrxCreateAction,
  createReducer as ngrxCreateReducer,
  createSelectorFactory,
  MemoizedSelector,
  on as ngrxOn,
  props as ngrxProps,
  props,
} from '@ngrx/store';
import { Selector } from '@ngrx/store/src/models';
import stringify from 'fast-json-stable-stringify';

import { memoize } from './selector-utils';
import {
  ActionPayloadFromConfigFn,
  ActionScopedPayload,
  ActionsFromConfig,
  ConfigFn,
  ConfigObject,
  Reference,
} from './types';

/* eslint-disable max-lines-per-function */

type RemoveVoid<T> = T extends void ? never : T;

/**
 * ngrxEvent is an action that doesn't change state
 * @example
 * const { actions, reducer } = createActions('namespace', {
 *   action1: ngrxEvent<void>(),
 *   action2: ngrxEvent<{ value: string }>(),
 * });
 */
export const ngrxEvent = <T>(): RemoveVoid<T> extends void
  ? <S>(state: S) => S
  : <S>(state: S, payload: T) => S => <any>((state: any) => state);

export const createSimpleAction = <Namespace extends string, ActionName extends string>(
  namespace: Namespace,
  actionName: ActionName,
) => ngrxCreateAction(`[${namespace}] ${actionName}`);

export const createSimpleActionWithPayload = <Payload extends {}>(
  namespace: string,
  actionName: string,
) => ngrxCreateAction(`[${namespace}] ${actionName}`, props<{ payload: Payload }>());

/**
 * @see {@link createActionsWithReducer}
 */
export const createActions = <State, T extends ConfigObject<any>>(
  namespace: string,
  actions: T,
): ActionsFromConfig<T> => {
  const entries = <Array<[Extract<keyof T, string>, ConfigFn<State>]>>Object.entries(actions);
  return entries.reduce((acc, [actionName, handler]) => {
    const actionCreator = ngrxCreateAction(
      `[${namespace}] ${actionName}`,
      ngrxProps<{ payload: ActionScopedPayload<ActionPayloadFromConfigFn<typeof handler>> }>(),
    );

    // @ts-ignore
    acc[actionName] = Object.assign(actionCreator, { namespace, actionName });

    return acc;
  }, <ActionsFromConfig<T>>{});
};

/**
 * @see {@link createActionsWithReducer}
 */
const createReducer = <S, C extends ConfigObject<S>>(
  state: S,
  config: C,
  actions: ActionsFromConfig<C>,
) => {
  const actionEntries = Object.entries(actions);
  return ngrxCreateReducer(
    state,
    ...actionEntries.map(([key, action]) =>
      ngrxOn(
        action,
        (
          currentState: S,
          actionInstance: ActionType<
            ActionCreator<
              string,
              (options: { payload?: ActionScopedPayload<any> }) => {
                payload?: ActionScopedPayload<any>;
              }
            >
          >,
        ) => config[key](currentState, actionInstance.payload),
      ),
    ),
  );
};
export const arrayIsDifferent = (arr1: any[], arr2: any[]): boolean => {
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) return true;
  if (arr1.length !== arr2.length) return true;
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i] && !Array.isArray(arr1[i])) {
      return true;
    }
    if (Array.isArray(arr1[i]) && arrayIsDifferent(arr1[i], arr2[i])) return true;
  }
  return false;
};
const EMPTY_SYMBOL = Symbol('EMPTY');
export const createSelectorWithReferences = <State extends object, Result = any>(
  baseStateSelector: MemoizedSelector<State, Result>,
  referenceSelectors: Array<Reference<any>> | null,
  resultFn: (state: Result) => Result,
): MemoizedSelector<State, Result> => {
  const referencesConfig = referenceSelectors || [];
  const selectorCache = new Map<
    number,
    Record<number, [cachedPropertyValue: any, cachedSelector: (state: any) => any]>
  >();
  const entityWithReferencesCache = new WeakMap<object, object>();
  let previousStateChunks: any[] | null = null;
  let resultCache: any = null;

  const selectorFactory = createSelectorFactory(memoize({ projectorFn: resultFn }), {
    stateFn: (rootState, selectors, __props: undefined, memoizedProjector) => {
      const [mainSelector] = <Array<Selector<any, any>>>selectors;
      const mainState = mainSelector(rootState);
      const isArray = Array.isArray(mainState);
      const mainStateArr = isArray ? mainState : [mainState];
      const referencesCount = referencesConfig.length;
      const currentStateChunks: any[] = [];
      const result = mainStateArr.map((entity: any, entityIndex: number) => {
        const entityRefSelectorCache = selectorCache.get(entityIndex) || {};
        currentStateChunks.push(entity);
        if (entity === null || entity === undefined) {
          return entity;
        }

        const offset = (referencesCount + 1) * entityIndex;
        const entityWithReferences = { ...entity };
        let isNewEntity = !previousStateChunks || entity !== previousStateChunks[offset];
        for (let referenceIndex = 0; referenceIndex < referencesCount; referenceIndex++) {
          const { mapFrom, mapTo, source } = referencesConfig[referenceIndex];
          let referenceValue = EMPTY_SYMBOL;
          if (entityRefSelectorCache[referenceIndex]) {
            const [cachedValue, cachedSelector] = entityRefSelectorCache[referenceIndex];
            if (cachedValue === entity[mapFrom]) {
              referenceValue = cachedSelector(rootState);
              if (referenceValue !== previousStateChunks![offset + referenceIndex + 1]) {
                isNewEntity = true;
              }
            }
          }
          if (referenceValue === EMPTY_SYMBOL) {
            const selector = source(entity[mapFrom]);
            entityRefSelectorCache[referenceIndex] = [entity[mapFrom], selector];
            referenceValue = selector(rootState);
            isNewEntity = true;
          }
          entityWithReferences[mapTo] = referenceValue;
          currentStateChunks.push(referenceValue);
        }
        if (isNewEntity) {
          entityWithReferencesCache.set(entity, entityWithReferences);
        }
        selectorCache.set(entityIndex, entityRefSelectorCache);
        if (!isNewEntity && entityWithReferencesCache.has(entity)) {
          return entityWithReferencesCache.get(entity);
        } else {
          return entityWithReferences;
        }
      });

      const cachedArgsChanged =
        !previousStateChunks ||
        currentStateChunks.length !== previousStateChunks.length ||
        previousStateChunks.some((arg, i) =>
          Array.isArray(currentStateChunks[i])
            ? arrayIsDifferent(currentStateChunks[i], arg)
            : currentStateChunks[i] !== arg,
        );

      if (cachedArgsChanged) {
        previousStateChunks = currentStateChunks;
        resultCache = isArray ? result : result[0];
      }

      return memoizedProjector.memoized(resultCache);
    },
  });

  return <any>(
    selectorFactory([baseStateSelector, ...referencesConfig.map(({ source }) => source), resultFn])
  );
};

/**
 * This helper reduces action/reducer boilerplate
 * @example
 * const initialState = { prop1: 1, prop2: 'a' };
 *
 *
 * const { actions, reducer } = createActionsWithReducer('namespace', initialState, {
 *   action1: state => ({ ...state, prop1: 2 }),
 *   action2: (state, payload: { value: string }) => ({ ...state, prop2: payload.value }),
 * });
 *
 * is equal to
 *
 * const action1 = ngrxCreateAction('[namespace] action1');
 * const action2 = ngrxCreateAction('[namespace] action2', props<{ value: string }>());
 * const reducer = ngrxCreateReducer(
 *   initialState,
 *   ngrxOn(action1, state => ({ ...state, prop1: 2 })),
 *   ngrxOn(action2, (state, payload) => ({ ...state, prop2: payload.value })),
 * );
 */
export const createActionsWithReducer = <
  Namespace extends string,
  State,
  ActionsConfig extends ConfigObject<State>,
>(
  namespace: Namespace,
  state: State,
  actionReducerMap: ActionsConfig,
) => {
  const actions = createActions<State, ActionsConfig>(namespace, actionReducerMap);
  const reducer = createReducer<State, ActionsConfig>(state, actionReducerMap, actions);

  return { actions, reducer };
};

/**
 * @example
 * pipeState(
 *  state,
 *  next => adapter.someOperation(next),
 *  next => adapter.otherOperation(next),
 *  ...
 * )
 */
export const pipeState = <T>(state: T, ...transformFns: Array<(s: T) => T>): T =>
  transformFns.reduce((previousState, transformFn) => transformFn(previousState), state);

/**
 * Wrapper around stable stringify (sorted props)
 */
export const objectToScope = (value: object) => stringify(value);

export const scopeFactory = <T>(context: string, id: T) => `context:${context}|id:${id}`;

/**
 * This helper should make it easier to map references to corresponding actions
 * @example
 * const apiReferences: FullCandidateReferences = references
 * const referenceActionsFactory = referencesToActionsFactory<FullCandidateReferences>()([
 *   { type: 'Admin', action: UserActions.references },
 *   { type: 'Stage', action: StageActions.references },
 *   { type: 'Offer', action: JobsBriefActions.references },
 *   { type: 'ReferralReferrer', action: CandidateReferrerActions.references },
 * ]);
 *
 * Effect
 * ...
 * switchMap(({candidate, references}) => [
 *   ...referenceActionsFactory(references),
 *   FullCandidateActions.fetchOneSuccess({ payload: { data: candidate } }),
 * ]),
 * ...
 */
export const referencesToActionsFactory =
  <ReferenceUnion extends { type: string }>() =>
  <
    Config extends {
      type: ReferenceUnion['type'];
      action: ActionCreator<
        string,
        (arg: { payload: { data: any } }) => { payload: { data: any } }
      >;
    },
  >(
    config: Config[],
  ) => {
    const referencesMainMap = config.reduce((acc, item) => {
      acc[item.type] = [];
      return acc;
    }, <Record<string, any[]>>{});

    return (references: ReferenceUnion[]) => {
      let referencesMap = referencesMainMap;
      for (const reference of references) {
        if (reference.type in referencesMap) {
          referencesMap = {
            ...referencesMap,
            [reference.type]: [...referencesMap[reference.type], reference],
          };
        }
      }

      return <Array<ReturnType<Config['action']>>>(
        config.map(({ type, action }) => action({ payload: { data: referencesMap[type] } }))
      );
    };
  };
